/**
 * Writes a zip file, by hand, in about a hundred lines.
 *
 * ## Why not a library
 *
 * The photos are already JPEG, which is already compressed: deflating them
 * again saves around one per cent and costs a phone real battery. So the only
 * thing needed here is the "stored" mode — put the bytes in, write the index —
 * and that is small enough to own. A zip library would ship a compressor that
 * this tool would deliberately never use.
 *
 * ## Why a zip at all
 *
 * A browser can only hand you one file, and the export is a data file plus
 * however many photos. Zip is the one container every phone and every desktop
 * opens without installing anything.
 */

/** One file inside the archive. */
export interface ZipEntry {
  /** Path inside the zip, with forward slashes. */
  name: string;
  data: Uint8Array;
  /** Timestamp recorded for the file. */
  at: Date;
}

/** The information needed to size a stored ZIP without allocating its bytes. */
export interface ZipSizeEntry {
  name: string;
  size: number;
}

/**
 * Exact size of the archive this writer will create.
 *
 * ZIP entries are stored, not compressed, so the payload size is simply added
 * to the local header, central-directory header and end-record overhead.
 */
export function zipSize(entries: readonly ZipSizeEntry[]): number {
  const encoder = new TextEncoder();
  const localAndData = entries.reduce(
    (total, entry) => total + 30 + encoder.encode(entry.name).length + entry.size,
    0,
  );
  const central = entries.reduce(
    (total, entry) => total + 46 + encoder.encode(entry.name).length,
    0,
  );
  return localAndData + central + 22;
}

let TABLE: Int32Array | null = null;

/** The checksum every zip entry carries, so a truncated copy is detectable. */
export function crc32(data: Uint8Array): number {
  if (TABLE === null) {
    TABLE = new Int32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      TABLE[n] = c;
    }
  }
  let c = -1;
  for (const byte of data) {
    // The mask keeps the index inside 0..255, so the fallback never happens;
    // it is there because the compiler cannot know that.
    c = (TABLE[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8);
  }
  return (c ^ -1) >>> 0;
}

/**
 * The clock format MS-DOS used in 1989, which zip still carries.
 *
 * Seconds only go in twos — the field is five bits — and years start at 1980.
 * Anything older than that gets clamped rather than wrapping around to 2043.
 */
function dosTime(at: Date): number {
  return (at.getHours() << 11) | (at.getMinutes() << 5) | (at.getSeconds() >> 1);
}

function dosDate(at: Date): number {
  const year = Math.max(1980, at.getFullYear());
  return ((year - 1980) << 9) | ((at.getMonth() + 1) << 5) | at.getDate();
}

/** Marks the file name as UTF-8, so accents survive the trip. */
const UTF8_NAMES = 0x0800;
/** Method 0: the bytes go in as they are. */
const STORED = 0;

/** Builds the whole archive in memory. */
export function buildZip(entries: readonly ZipEntry[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const time = dosTime(entry.at);
    const date = dosDate(entry.at);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // needs an unzip that understands version 2.0
    lv.setUint16(6, UTF8_NAMES, true);
    lv.setUint16(8, STORED, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, entry.data.length, true);
    lv.setUint32(22, entry.data.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);

    const head = new Uint8Array(46 + name.length);
    const hv = new DataView(head.buffer);
    hv.setUint32(0, 0x02014b50, true);
    hv.setUint16(4, 20, true);
    hv.setUint16(6, 20, true);
    hv.setUint16(8, UTF8_NAMES, true);
    hv.setUint16(10, STORED, true);
    hv.setUint16(12, time, true);
    hv.setUint16(14, date, true);
    hv.setUint32(16, crc, true);
    hv.setUint32(20, entry.data.length, true);
    hv.setUint32(24, entry.data.length, true);
    hv.setUint16(28, name.length, true);
    hv.setUint32(38, 0, true); // an ordinary file, no permissions carried over
    hv.setUint32(42, offset, true);
    head.set(name, 46);

    parts.push(local, entry.data);
    central.push(head);
    offset += local.length + entry.data.length;
  }

  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const all = [...parts, ...central, end];
  const total = zipSize(entries.map(({ name, data }) => ({ name, size: data.length })));
  const zip = new Uint8Array(total);
  let cursor = 0;
  for (const part of all) {
    zip.set(part, cursor);
    cursor += part.length;
  }
  return zip;
}
